import { Money } from '../ui/Money';
import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Switch } from '../ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Users, Plus, Trash2, ChevronDown, ChevronUp, Phone, Edit2 } from 'lucide-react';
import { Badge } from '../ui/badge';
import DeleteConfirmDialog from '../DeleteConfirmDialog';

const RELATION_OPTIONS = [
  'Spouse',
  'Child',
  'Parent',
  'Sibling',
  'Other'
];

const FREQ_OPTIONS = [
  { value: 'monthly', label: 'Monthly (÷1)', divisor: 1 },
  { value: 'yearly', label: 'Yearly (÷12)', divisor: 12 },
  { value: '2_years', label: 'Every 2 years (÷24)', divisor: 24 },
  { value: 'custom', label: 'Custom (÷ months)', divisor: null },
];

const freqDivisor = (freq, customMonths) => {
  if (freq === 'custom') {
    const m = parseFloat(customMonths) || 0;
    return m >= 1 ? m : 12;
  }
  const opt = FREQ_OPTIONS.find(o => o.value === freq);
  return opt?.divisor || 12;
};

// Full monthly, company-borne cost of one dependent (sponsored insurance + extra items).
const dependentMonthlyCost = (dep) => {
  let m = 0;
  if (dep.insurance_sponsored) {
    m += (parseFloat(dep.insurance_cost) || 0) / freqDivisor(dep.insurance_frequency || 'yearly');
  }
  (dep.cost_items || []).forEach((it) => {
    m += (parseFloat(it.amount) || 0) / freqDivisor(it.frequency || 'yearly', it.custom_months);
  });
  return m;
};

const emptyDependent = {
  name: '',
  relation: '',
  passport_number: '',
  passport_expiry: '',
  emirates_id: '',
  emirates_id_expiry: '',
  phone: '',
  insurance_sponsored: false,
  insurance_cost: 0,
  insurance_frequency: 'yearly',
  cost_items: []
};

const DependentsSection = ({ formData, setFormData }) => {
  const [showAddForm, setShowAddForm] = useState(false);
  const [expandedDependents, setExpandedDependents] = useState({});
  const [editingIndex, setEditingIndex] = useState(null);
  const [editingDependent, setEditingDependent] = useState(null);
  const [newDependent, setNewDependent] = useState({ ...emptyDependent });
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [dependentToDelete, setDependentToDelete] = useState(null);

  const dependents = formData.dependents || [];

  const addDependent = () => {
    if (!newDependent.name || !newDependent.relation) {
      alert('Please enter at least the name and relation');
      return;
    }

    const dependentWithId = {
      ...newDependent,
      id: Date.now().toString(),
      insurance_cost: newDependent.insurance_sponsored ? parseFloat(newDependent.insurance_cost) || 0 : 0
    };

    setFormData(prev => ({
      ...prev,
      dependents: [...(prev.dependents || []), dependentWithId]
    }));

    setNewDependent({ ...emptyDependent });
    setShowAddForm(false);
  };

  const removeDependent = (index) => {
    const dep = dependents[index];
    setDependentToDelete({ index, name: dep.name, relation: dep.relation });
    setDeleteConfirmOpen(true);
  };

  const confirmRemoveDependent = () => {
    if (dependentToDelete === null) return;
    
    setFormData(prev => ({
      ...prev,
      dependents: prev.dependents.filter((_, i) => i !== dependentToDelete.index)
    }));
    setDependentToDelete(null);
  };

  const startEdit = (index, dep) => {
    setEditingIndex(index);
    setEditingDependent({ ...dep });
  };

  const cancelEdit = () => {
    setEditingIndex(null);
    setEditingDependent(null);
  };

  const saveEdit = () => {
    if (!editingDependent.name || !editingDependent.relation) {
      alert('Please enter at least the name and relation');
      return;
    }

    const updatedDependent = {
      ...editingDependent,
      insurance_cost: editingDependent.insurance_sponsored ? parseFloat(editingDependent.insurance_cost) || 0 : 0
    };

    setFormData(prev => ({
      ...prev,
      dependents: prev.dependents.map((dep, i) => 
        i === editingIndex ? updatedDependent : dep
      )
    }));

    setEditingIndex(null);
    setEditingDependent(null);
  };

  const toggleExpand = (index) => {
    setExpandedDependents(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  // Calculate total dependent insurance cost (annual, sponsored only)
  const totalDependentInsurance = dependents.reduce((sum, dep) => {
    return sum + (dep.insurance_sponsored ? (parseFloat(dep.insurance_cost) || 0) : 0);
  }, 0);

  // Full monthly dependent cost added to Cost to Company (insurance + air ticket + custom).
  const totalMonthlyDependentCost = dependents.reduce((sum, dep) => sum + dependentMonthlyCost(dep), 0);

  // Check for expiring documents (within 90 days)
  const getExpiryStatus = (dateStr) => {
    if (!dateStr) return null;
    const expiry = new Date(dateStr);
    const today = new Date();
    const daysUntilExpiry = Math.ceil((expiry - today) / (1000 * 60 * 60 * 24));
    
    if (daysUntilExpiry < 0) return { status: 'expired', days: Math.abs(daysUntilExpiry) };
    if (daysUntilExpiry <= 30) return { status: 'critical', days: daysUntilExpiry };
    if (daysUntilExpiry <= 90) return { status: 'warning', days: daysUntilExpiry };
    return { status: 'ok', days: daysUntilExpiry };
  };

  const getExpiryBadge = (dateStr, label) => {
    const status = getExpiryStatus(dateStr);
    if (!status) return null;
    
    if (status.status === 'expired') {
      return <Badge className="bg-red-500 text-xs ml-1">Expired {status.days}d ago</Badge>;
    }
    if (status.status === 'critical') {
      return <Badge className="bg-red-500 text-xs ml-1">{status.days}d left</Badge>;
    }
    if (status.status === 'warning') {
      return <Badge className="bg-amber-500 text-xs ml-1">{status.days}d left</Badge>;
    }
    return null;
  };

  // Form fields component (reused for add and edit)
  const DependentForm = ({ data, setData, isEdit = false }) => (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label>Name *</Label>
          <Input
            value={data.name}
            onChange={(e) => setData(prev => ({ ...prev, name: e.target.value }))}
            placeholder="Full name"
            data-testid={isEdit ? "edit-dependent-name" : "dependent-name-input"}
          />
        </div>
        <div>
          <Label>Relation *</Label>
          <Select
            value={data.relation}
            onValueChange={(value) => setData(prev => ({ ...prev, relation: value }))}
          >
            <SelectTrigger data-testid={isEdit ? "edit-dependent-relation" : "dependent-relation-select"}>
              <SelectValue placeholder="Select relation" />
            </SelectTrigger>
            <SelectContent>
              {RELATION_OPTIONS.map((rel) => (
                <SelectItem key={rel} value={rel}>{rel}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label>Passport Number</Label>
          <Input
            value={data.passport_number}
            onChange={(e) => setData(prev => ({ ...prev, passport_number: e.target.value }))}
            placeholder="Passport number"
          />
        </div>
        <div>
          <Label>Passport Expiry</Label>
          <Input
            type="date"
            value={data.passport_expiry}
            onChange={(e) => setData(prev => ({ ...prev, passport_expiry: e.target.value }))}
          />
          {data.passport_expiry && getExpiryBadge(data.passport_expiry)}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label>Emirates ID</Label>
          <Input
            value={data.emirates_id}
            onChange={(e) => setData(prev => ({ ...prev, emirates_id: e.target.value }))}
            placeholder="784-XXXX-XXXXXXX-X"
          />
        </div>
        <div>
          <Label>EID Expiry</Label>
          <Input
            type="date"
            value={data.emirates_id_expiry}
            onChange={(e) => setData(prev => ({ ...prev, emirates_id_expiry: e.target.value }))}
          />
          {data.emirates_id_expiry && getExpiryBadge(data.emirates_id_expiry)}
        </div>
      </div>

      <div>
        <Label>Phone Number</Label>
        <Input
          value={data.phone}
          onChange={(e) => setData(prev => ({ ...prev, phone: e.target.value }))}
          placeholder="+971 XX XXX XXXX"
        />
      </div>

      {/* Insurance Section */}
      <div className="border-t pt-4">
        <div className="flex items-center justify-between mb-3">
          <div>
            <Label className="text-base">Insurance Sponsored by Company?</Label>
            <p className="text-xs text-gray-500">If yes, the cost will be added to employee's overhead</p>
          </div>
          <Switch
            checked={data.insurance_sponsored}
            onCheckedChange={(checked) => setData(prev => ({ 
              ...prev, 
              insurance_sponsored: checked,
              insurance_cost: checked ? prev.insurance_cost : 0
            }))}
            data-testid={isEdit ? "edit-dependent-insurance-switch" : "dependent-insurance-switch"}
          />
        </div>

        {data.insurance_sponsored && (
          <div className="bg-amber-50 p-3 rounded-lg space-y-2">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label>Insurance Cost (AED)</Label>
                <Input
                  type="number"
                  value={data.insurance_cost}
                  onChange={(e) => setData(prev => ({ ...prev, insurance_cost: e.target.value }))}
                  placeholder="e.g., 2400"
                  className="mt-1"
                  data-testid={isEdit ? "edit-dependent-insurance-cost" : "dependent-insurance-cost-input"}
                />
              </div>
              <div>
                <Label>Applicable</Label>
                <Select
                  value={data.insurance_frequency || 'yearly'}
                  onValueChange={(v) => setData(prev => ({ ...prev, insurance_frequency: v }))}
                >
                  <SelectTrigger className="mt-1" data-testid={isEdit ? "edit-dependent-insurance-freq" : "dependent-insurance-freq"}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {FREQ_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            {data.insurance_frequency === 'custom' && (
              <div>
                <Label>Custom divisor (months)</Label>
                <Input type="number" min="1" value={data.insurance_custom_months || ''}
                  onChange={(e) => setData(prev => ({ ...prev, insurance_custom_months: e.target.value }))}
                  placeholder="e.g., 36" className="mt-1" />
              </div>
            )}
            <p className="text-xs text-amber-600">
              Monthly: <Money value={(parseFloat(data.insurance_cost) || 0) / freqDivisor(data.insurance_frequency || 'yearly', data.insurance_custom_months)} digits={2} />
            </p>
          </div>
        )}
      </div>

      {/* Additional Dependent Costs (air ticket + custom) */}
      <div className="border-t pt-4">
        <div className="flex items-center justify-between mb-2">
          <div>
            <Label className="text-base">Additional Costs</Label>
            <p className="text-xs text-gray-500">Air ticket and any other company-borne dependent costs</p>
          </div>
          <div className="flex gap-2">
            <Button type="button" size="sm" variant="outline"
              onClick={() => setData(prev => ({ ...prev, cost_items: [...(prev.cost_items || []), { id: Date.now().toString(), label: 'Air Ticket', amount: '', frequency: '2_years', custom_months: '' }] }))}
              data-testid={isEdit ? "edit-add-air-ticket" : "add-air-ticket"}>
              <Plus className="h-4 w-4 mr-1" /> Air Ticket
            </Button>
            <Button type="button" size="sm" variant="outline"
              onClick={() => setData(prev => ({ ...prev, cost_items: [...(prev.cost_items || []), { id: Date.now().toString(), label: '', amount: '', frequency: 'yearly', custom_months: '' }] }))}
              data-testid={isEdit ? "edit-add-cost-item" : "add-cost-item"}>
              <Plus className="h-4 w-4 mr-1" /> Custom
            </Button>
          </div>
        </div>

        {(data.cost_items || []).length === 0 ? (
          <p className="text-xs text-gray-400 italic">No additional costs added.</p>
        ) : (
          <div className="space-y-2">
            {(data.cost_items || []).map((it, ci) => {
              const patch = (p) => setData(prev => ({
                ...prev,
                cost_items: (prev.cost_items || []).map((x, i) => i === ci ? { ...x, ...p } : x),
              }));
              const removeItem = () => setData(prev => ({
                ...prev, cost_items: (prev.cost_items || []).filter((_, i) => i !== ci),
              }));
              const monthly = (parseFloat(it.amount) || 0) / freqDivisor(it.frequency || 'yearly', it.custom_months);
              return (
                <div key={it.id || ci} className="bg-gray-50 rounded-lg p-2 border">
                  <div className="grid grid-cols-12 gap-2 items-end">
                    <div className="col-span-4">
                      <Label className="text-xs">Label</Label>
                      <Input value={it.label} onChange={(e) => patch({ label: e.target.value })}
                        placeholder="e.g., Air Ticket" className="mt-1 h-9"
                        data-testid={`cost-item-label-${ci}`} />
                    </div>
                    <div className="col-span-3">
                      <Label className="text-xs">Amount (AED)</Label>
                      <Input type="number" value={it.amount} onChange={(e) => patch({ amount: e.target.value })}
                        placeholder="0" className="mt-1 h-9" data-testid={`cost-item-amount-${ci}`} />
                    </div>
                    <div className="col-span-4">
                      <Label className="text-xs">Applicable</Label>
                      <Select value={it.frequency || 'yearly'} onValueChange={(v) => patch({ frequency: v })}>
                        <SelectTrigger className="mt-1 h-9" data-testid={`cost-item-freq-${ci}`}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {FREQ_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="col-span-1 flex justify-end">
                      <Button type="button" variant="ghost" size="sm" onClick={removeItem}
                        data-testid={`remove-cost-item-${ci}`} title="Remove">
                        <Trash2 className="h-4 w-4 text-red-500" />
                      </Button>
                    </div>
                  </div>
                  {it.frequency === 'custom' && (
                    <div className="mt-2">
                      <Label className="text-xs">Custom divisor (months)</Label>
                      <Input type="number" min="1" value={it.custom_months || ''}
                        onChange={(e) => patch({ custom_months: e.target.value })}
                        placeholder="e.g., 36" className="mt-1 h-9 max-w-[180px]" />
                    </div>
                  )}
                  <p className="text-xs text-indigo-500 mt-1">Monthly: <Money value={monthly} digits={2} /></p>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className="border rounded-lg p-4 bg-gradient-to-br from-indigo-50 to-purple-50">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold flex items-center gap-2 text-indigo-700">
          <Users className="h-5 w-5" />
          Dependents
          {dependents.length > 0 && (
            <Badge variant="secondary">{dependents.length}</Badge>
          )}
        </h3>
        <Button
          type="button"
          size="sm"
          onClick={() => setShowAddForm(!showAddForm)}
          className="bg-indigo-600 hover:bg-indigo-700"
          data-testid="add-dependent-btn"
        >
          <Plus className="h-4 w-4 mr-1" />
          Add Dependent
        </Button>
      </div>

      {/* Total Insurance Cost Summary */}
      {totalMonthlyDependentCost > 0 && (
        <div className="bg-white rounded-lg p-3 mb-4 border border-indigo-200">
          <div className="flex justify-between items-center">
            <span className="text-sm text-gray-600">Total Dependent Insurance (Annual)</span>
            <span className="font-bold text-indigo-600"><Money value={totalDependentInsurance} /></span>
          </div>
          <div className="flex justify-between items-center text-sm">
            <span className="text-gray-500">Monthly Cost (added to Cost to Company)</span>
            <span className="text-indigo-500"><Money value={totalMonthlyDependentCost} digits={2} /></span>
          </div>
        </div>
      )}

      {/* Add Dependent Form */}
      {showAddForm && (
        <div className="bg-white rounded-lg p-4 mb-4 border border-indigo-200">
          <h4 className="font-medium text-indigo-700 mb-3">Add New Dependent</h4>
          <DependentForm data={newDependent} setData={setNewDependent} />
          <div className="flex gap-2 justify-end mt-4">
            <Button type="button" variant="outline" onClick={() => setShowAddForm(false)}>
              Cancel
            </Button>
            <Button 
              type="button" 
              onClick={addDependent}
              className="bg-indigo-600 hover:bg-indigo-700"
              data-testid="save-dependent-btn"
            >
              Add Dependent
            </Button>
          </div>
        </div>
      )}

      {/* Edit Dependent Dialog */}
      <Dialog open={editingIndex !== null} onOpenChange={(open) => !open && cancelEdit()}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit2 className="h-5 w-5 text-indigo-600" />
              Edit Dependent - {editingDependent?.name}
            </DialogTitle>
          </DialogHeader>
          {editingDependent && (
            <>
              <DependentForm 
                data={editingDependent} 
                setData={setEditingDependent} 
                isEdit={true}
              />
              <div className="flex gap-2 justify-end mt-4">
                <Button type="button" variant="outline" onClick={cancelEdit}>
                  Cancel
                </Button>
                <Button 
                  type="button" 
                  onClick={saveEdit}
                  className="bg-indigo-600 hover:bg-indigo-700"
                  data-testid="update-dependent-btn"
                >
                  Update Dependent
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Dependents List */}
      {dependents.length === 0 ? (
        <p className="text-sm text-gray-500 italic text-center py-4">No dependents added</p>
      ) : (
        <div className="space-y-2">
          {dependents.map((dep, index) => {
            const passportExpiry = getExpiryStatus(dep.passport_expiry);
            const eidExpiry = getExpiryStatus(dep.emirates_id_expiry);
            const hasExpiryWarning = (passportExpiry && ['expired', 'critical', 'warning'].includes(passportExpiry.status)) ||
                                     (eidExpiry && ['expired', 'critical', 'warning'].includes(eidExpiry.status));
            
            return (
              <div 
                key={dep.id || index} 
                className={`bg-white rounded-lg border overflow-hidden ${
                  hasExpiryWarning ? 'border-amber-300 border-l-4 border-l-amber-500' : 'border-indigo-200'
                }`}
              >
                {/* Header - Always visible */}
                <div 
                  className="flex items-center justify-between p-3 cursor-pointer hover:bg-gray-50"
                  onClick={() => toggleExpand(index)}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center">
                      <Users className="h-5 w-5 text-indigo-600" />
                    </div>
                    <div>
                      <p className="font-medium">{dep.name}</p>
                      <div className="flex items-center gap-2 flex-wrap">
                        <Badge variant="outline" className="text-xs">{dep.relation}</Badge>
                        {dep.insurance_sponsored && (
                          <Badge className="bg-amber-500 text-xs">Insurance: <Money value={dep.insurance_cost} />/yr</Badge>
                        )}
                        {passportExpiry?.status === 'expired' && (
                          <Badge className="bg-red-500 text-xs">Passport Expired</Badge>
                        )}
                        {passportExpiry?.status === 'critical' && (
                          <Badge className="bg-red-500 text-xs">Passport: {passportExpiry.days}d</Badge>
                        )}
                        {eidExpiry?.status === 'expired' && (
                          <Badge className="bg-red-500 text-xs">EID Expired</Badge>
                        )}
                        {eidExpiry?.status === 'critical' && (
                          <Badge className="bg-red-500 text-xs">EID: {eidExpiry.days}d</Badge>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        startEdit(index, dep);
                      }}
                      data-testid={`edit-dependent-${index}-btn`}
                      title="Edit dependent"
                    >
                      <Edit2 className="h-4 w-4 text-blue-500" />
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        removeDependent(index);
                      }}
                      data-testid={`remove-dependent-${index}-btn`}
                      title="Remove dependent"
                    >
                      <Trash2 className="h-4 w-4 text-red-500" />
                    </Button>
                    {expandedDependents[index] ? (
                      <ChevronUp className="h-5 w-5 text-gray-400" />
                    ) : (
                      <ChevronDown className="h-5 w-5 text-gray-400" />
                    )}
                  </div>
                </div>

                {/* Expanded Details */}
                {expandedDependents[index] && (
                  <div className="border-t p-3 bg-gray-50 grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
                    {dep.phone && (
                      <div className="flex items-center gap-2">
                        <Phone className="h-4 w-4 text-gray-400" />
                        <span>{dep.phone}</span>
                      </div>
                    )}
                    {dep.passport_number && (
                      <div>
                        <span className="text-gray-500">Passport:</span>
                        <span className="ml-1 font-medium">{dep.passport_number}</span>
                        {dep.passport_expiry && (
                          <div className="text-xs mt-1">
                            <span className="text-gray-500">Expiry: </span>
                            <span className={passportExpiry?.status === 'expired' ? 'text-red-600 font-medium' : 
                                           passportExpiry?.status === 'critical' ? 'text-red-600' :
                                           passportExpiry?.status === 'warning' ? 'text-amber-600' : ''}>
                              {new Date(dep.passport_expiry).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                            </span>
                            {getExpiryBadge(dep.passport_expiry)}
                          </div>
                        )}
                      </div>
                    )}
                    {dep.emirates_id && (
                      <div>
                        <span className="text-gray-500">EID:</span>
                        <span className="ml-1 font-medium">{dep.emirates_id}</span>
                        {dep.emirates_id_expiry && (
                          <div className="text-xs mt-1">
                            <span className="text-gray-500">Expiry: </span>
                            <span className={eidExpiry?.status === 'expired' ? 'text-red-600 font-medium' : 
                                           eidExpiry?.status === 'critical' ? 'text-red-600' :
                                           eidExpiry?.status === 'warning' ? 'text-amber-600' : ''}>
                              {new Date(dep.emirates_id_expiry).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                            </span>
                            {getExpiryBadge(dep.emirates_id_expiry)}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Delete Dependent Confirmation Dialog */}
      <DeleteConfirmDialog
        open={deleteConfirmOpen}
        onOpenChange={setDeleteConfirmOpen}
        title="Remove Dependent"
        description="Are you sure you want to remove this dependent? This will also remove any associated insurance costs."
        itemName={dependentToDelete ? `${dependentToDelete.name} (${dependentToDelete.relation})` : ''}
        onConfirm={confirmRemoveDependent}
      />
    </div>
  );
};

export default DependentsSection;
